<template>
	<div
		class="voice-asset-widget space-y-2 rounded-md border border-gray-700 bg-gray-900 p-4 shadow-sm"
	>
		<div class="flex items-center justify-between">
			<label class="text-sm font-medium text-gray-200">{{ label }}</label>
			<span
				v-if="hasAsset && assetDetail && assetDetail.voice_asset_id"
				class="text-xs text-gray-400"
			>
				{{ assetDetail.voice_asset_id }}
			</span>
		</div>

		<div
			v-if="loadingDetail || uploading"
			class="flex items-center gap-2 text-sm text-gray-400"
		>
			<i class="el-icon-loading animate-spin" />
			<span>{{ uploading ? "Uploading audio..." : "Loading audio..." }}</span>
		</div>

		<template v-else>
			<div
				v-if="!hasAsset"
				class="flex flex-col gap-3 rounded-md border border-dashed border-gray-600 bg-gray-800 p-4 text-sm text-gray-400"
			>
				<p>Upload a WAV or MP3 clip up to 60 seconds. The file becomes a reusable voice asset.</p>
				<div class="flex flex-wrap items-center gap-2">
					<button
						type="button"
						class="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow hover:bg-blue-500 focus:outline-hidden focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
						@click="triggerFileSelect"
					>
						<i class="el-icon-upload" />
						<span>Select Audio</span>
					</button>
					<span class="text-xs text-gray-500">Supported: WAV, MP3</span>
				</div>
				<input
					ref="fileInput"
					type="file"
					class="hidden"
					accept="audio/wav,audio/mpeg,audio/mp3"
					@change="handleFileChange"
				/>
			</div>

			<div
				v-else
				class="flex items-center gap-3 rounded-md border border-gray-700 bg-gray-800 p-3"
			>
				<audio v-if="audioSource" :src="audioSource" controls class="flex-1" />
				<span v-else class="flex-1 text-sm text-red-400">Audio preview unavailable.</span>
				<button
					type="button"
					class="inline-flex items-center justify-center rounded-full border border-gray-600 p-2 text-gray-400 hover:border-red-400 hover:text-red-400 focus:outline-hidden focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
					@click="clearAsset"
				>
					<i class="el-icon-close h-4 w-4" />
					<span class="sr-only">Remove audio</span>
				</button>
			</div>

			<div v-if="metaInfo" class="text-xs text-gray-500">
				{{ metaInfo }}
			</div>
		</template>

		<p v-if="errorMessage" class="text-xs text-red-400">
			{{ errorMessage }}
		</p>
	</div>
</template>

<script>
import {
	uploadNotificationVoiceAsset,
	getNotificationVoiceAssetDetail,
} from "@/api";
import { extractErrorMessage } from "@/utils/errorUtils";

export default {
	name: "UploadAudioWidget",
	props: {
		value: {
			type: String,
			default: "",
		},
		label: {
			type: String,
			default: "Voice Asset",
		},
	},
	data() {
		return {
			internalVoiceAssetId: this.value || "",
			assetDetail: null,
			uploading: false,
			loadingDetail: false,
			errorMessage: "",
			allowedMimeTypes: [
				"audio/wav",
				"audio/wave",
				"audio/x-wav",
				"audio/mpeg",
				"audio/mp3",
			],
		};
	},
	computed: {
		hasAsset() {
			return !!this.internalVoiceAssetId;
		},
		audioSource() {
			if (this.assetDetail && this.assetDetail.gcs_url) {
				return this.assetDetail.gcs_url;
			}
			return "";
		},
		metaInfo() {
			if (!this.assetDetail) {
				return "";
			}
			const parts = [];
			if (typeof this.assetDetail.duration_sec === "number") {
				parts.push(`${Math.round(this.assetDetail.duration_sec)}s`);
			}
			if (typeof this.assetDetail.size_bytes === "number") {
				parts.push(this.readableBytes(this.assetDetail.size_bytes));
			}
			if (this.assetDetail.content_type) {
				parts.push(this.assetDetail.content_type);
			}
			return parts.join(" · ");
		},
	},
	watch: {
		value: {
			immediate: true,
			handler(newVal) {
				const normalized = newVal || "";
				if (normalized === this.internalVoiceAssetId) {
					if (normalized && !this.assetDetail && !this.loadingDetail) {
						this.fetchAssetDetail(normalized);
					}
					if (!normalized) {
						this.assetDetail = null;
					}
					return;
				}
				this.internalVoiceAssetId = normalized;
				if (normalized) {
					this.fetchAssetDetail(normalized);
				} else {
					this.assetDetail = null;
				}
			},
		},
	},
	methods: {
		triggerFileSelect() {
			const input = this.$refs.fileInput;
			if (input) {
				input.click();
			}
		},
		async handleFileChange(event) {
			const files = event?.target?.files;
			if (!files || !files.length) {
				return;
			}
			const file = files[0];
			if (!this.isMimeTypeAllowed(file.type)) {
				this.errorMessage = "Only WAV or MP3 files are supported.";
				this.resetFileInput();
				return;
			}
			await this.uploadFile(file);
		},
		isMimeTypeAllowed(type) {
			if (!type) {
				return true;
			}
			return this.allowedMimeTypes.includes(type);
		},
		async uploadFile(file) {
			this.uploading = true;
			this.errorMessage = "";
			try {
				const formData = new FormData();
				formData.append("file", file);
				const result = await uploadNotificationVoiceAsset(formData);
				this.assetDetail = result;
				this.emitValue(result.voice_asset_id || "");
			} catch (error) {
				this.errorMessage = extractErrorMessage(
					error,
					"Unable to upload audio asset."
				);
			} finally {
				this.uploading = false;
				this.resetFileInput();
			}
		},
		async fetchAssetDetail(voiceAssetId) {
			this.loadingDetail = true;
			this.errorMessage = "";
			try {
				const detail = await getNotificationVoiceAssetDetail(voiceAssetId);
				this.assetDetail = detail;
			} catch (error) {
				this.assetDetail = null;
				this.errorMessage = extractErrorMessage(
					error,
					"Unable to upload audio asset."
				);
			} finally {
				this.loadingDetail = false;
			}
		},
		clearAsset() {
			this.assetDetail = null;
			this.emitValue("");
		},
		emitValue(id) {
			this.internalVoiceAssetId = id;
			this.$emit("input", id);
			this.$emit("update:value", id);
			this.$emit("change", id);
		},
		resetFileInput() {
			const input = this.$refs.fileInput;
			if (input) {
				input.value = "";
			}
		},
		readableBytes(bytes) {
			if (typeof bytes !== "number") {
				return "";
			}
			if (bytes === 0) {
				return "0 B";
			}
			if (bytes < 1024) {
				return `${bytes} B`;
			}
			if (bytes < 1024 * 1024) {
				return `${(bytes / 1024).toFixed(1)} KB`;
			}
			return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
		},
	},
};
</script>
